// The bundle/module set math every dialog shares.
//
// `bundle-options` gives each bundle its `moduleIds`; the module catalogue (`modules`) turns
// those ids into cards. Everything here is pure, so the SAME answer decides what a dialog
// renders and what its submit path sends — the two cannot drift apart.
//
// These helpers used to be copy-pasted into five components. They now carry real rules
// (set equality, match finding), which is exactly the kind of thing that must not be
// maintained in five places.

// Group the modules by category, keeping the order the backend sent them in (sort_order), so
// both the categories and the cards inside them stay stable.
export const groupByCategory = (modules) => {
  const groups = {};
  (modules || []).forEach((m) => {
    const c = m.category || "Other";
    if (!groups[c]) {
      groups[c] = [];
    }
    groups[c].push(m);
  });
  return Object.entries(groups);
};

// Ids reach the browser from two endpoints: bundle-options casts them to int, the module
// catalogue and the company-feature read pass the driver's value straight through. Compare
// as strings so 10 and "10" can never read as two different things — every id comparison in
// the bundle surfaces goes through this, components included. It is exported for exactly
// that reason: a `===` in a component is how "10 vs \"10\"" turns into a confident lie about
// a live bundle being retired.
export const sameId = (a, b) => String(a) === String(b);

export const hasModuleId = (moduleIds, id) => (moduleIds || []).some((x) => sameId(x, id));

// The live bundle carrying this id, or null. Single lookup used by the set math AND by the
// components, so "is this bundle still live" has exactly one answer.
export const findBundleById = (bundleOptions, bundleId) =>
  (bundleOptions || []).find((b) => sameId(b.id, bundleId)) || null;

// Flip one module in a selection, returning a new array (Formik state, never mutated).
export const toggleModuleId = (moduleIds, id) => (hasModuleId(moduleIds, id)
  ? (moduleIds || []).filter((x) => !sameId(x, id))
  : [...(moduleIds || []), id]);

// A bundle only carries module ids. This is the raw list, straight from bundle-options — it
// is deliberately NOT filtered through the catalogue, so seeding a selection from it can
// never silently drop an id and turn "unchanged" into "the admin changed something".
export const moduleIdsForBundle = (bundleOptions, bundleId) => {
  const selected = findBundleById(bundleOptions, bundleId);
  return ((selected || {}).moduleIds || []).slice();
};

// The modules a bundle grants, resolved against the module catalogue and kept in catalogue
// order.
export const modulesForBundle = (bundleOptions, moduleListing, bundleId) => {
  const ids = moduleIdsForBundle(bundleOptions, bundleId);
  return (moduleListing || []).filter((m) => hasModuleId(ids, m.id));
};

const asSet = (ids) => Array.from(new Set((ids || []).map((id) => String(id)))).sort();

// Set equality: order and duplicates are meaningless, only membership counts.
export const sameModuleSet = (a, b) => {
  const x = asSet(a);
  const y = asSet(b);
  return x.length === y.length && x.every((v, i) => v === y[i]);
};

// The bundle whose module set is exactly `moduleIds`, or null. The backend refuses to create
// two bundles holding the same modules, so at most one can match here. Note that
// bundle-options lists LIVE + ACTIVE bundles only: a null means "no live bundle matches", NOT
// "the backend will accept a create" — the collision it rejects may be with a retired bundle,
// which is why the server's message still has to reach the admin.
export const findMatchingBundle = (bundleOptions, moduleIds) =>
  (bundleOptions || []).find((b) => sameModuleSet(b.moduleIds, moduleIds)) || null;

export const SELECTION_UNCHANGED = "unchanged";
export const SELECTION_EXISTING = "existing";
export const SELECTION_NEW = "new";

// What the switch grid currently describes, relative to the bundle in the dropdown:
//   UNCHANGED — exactly the selected bundle. Also the answer when the selected bundle is not
//               in bundle-options (nothing loaded yet, or the company sits on a deactivated
//               bundle): with nothing to compare against, a dialog must not invent a change.
//   EXISTING  — exactly some OTHER live bundle (`matched`). NOT an instruction to assign it:
//               see below. Re-creating it is the duplicate the backend 400s on, so the only
//               ways on are to select `matched` or to change the switches.
//   NEW       — no live bundle carries this set, so it has to be created before it can be
//               assigned to anybody.
export const describeSelection = (bundleOptions, bundleId, moduleIds) => {
  const selected = findBundleById(bundleOptions, bundleId);
  if (!selected || sameModuleSet(selected.moduleIds, moduleIds)) {
    return { status: SELECTION_UNCHANGED, selected, matched: selected };
  }
  const matched = findMatchingBundle(bundleOptions, moduleIds);
  return matched
    ? { status: SELECTION_EXISTING, selected, matched }
    : { status: SELECTION_NEW, selected, matched: null };
};

// THE INVARIANT, stated once so every surface can point at it:
//
//   No save may ever assign a bundle other than the one the Bundle dropdown is showing.
//
// SELECTION_EXISTING is therefore not a decision the code gets to make — it is an ambiguity.
// The switches name one live bundle and the dropdown names another, and only the admin can
// say which of the two they meant. Resolving it for them was a silent substitution: the
// control read "Basic", the banner mentioned "Basic + Traceability" in passing, and the save
// committed "Basic + Traceability". A banner documents a substitution; it does not consent
// to one. So this state BLOCKS, and the way out is one click ("Use it" puts the matched
// bundle in the dropdown, which makes the selection UNCHANGED).
export const matchedBundleName = (matched) => (matched && matched.name) || "another bundle";

// One wording for the two places that state the block — the editor's banner and the error on
// the Bundle field — so the banner cannot promise one outcome while the refused save
// describes a different one. Deliberately free of "save"/"approve": the wizard approves, the
// company surfaces save, and both render this same sentence.
export const matchedBundleBannerMessage = (matched) => {
  const name = matchedBundleName(matched);
  return `Bundle "${name}" already grants exactly these modules. A company is only ever`
    + ` assigned the bundle named in the Bundle field above, so the two have to agree before`
    + ` you can continue: choose "${name}" there — "Use it" does that — or change the modules`
    + ` to a combination no bundle grants yet.`;
};

export const matchedBundleFieldError = (matched) =>
  `These modules are exactly bundle "${matchedBundleName(matched)}". Choose it above, or`
  + ` change the modules, to continue.`;
